import React, { useEffect, useRef, useState } from "react";
import Spinner from "../components/Spinner";
import { Helmet } from "react-helmet-async";
import PublicHero from "../components/PublicHero";
import { Link } from "react-router-dom";
import { trackContactClick } from "../utils/tracking";

const contactPageSchema = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  name: "Contact Mission Movement",
  url: "https://missionmovement.nl/contact",
  inLanguage: "nl-NL",
  description:
    "Neem contact op met Mission Movement voor gerichte begeleiding richting Defensie, politie, brandweer, KMar of specialistische eenheden.",
  mainEntity: {
    "@type": "Organization",
    name: "Mission Movement",
    url: "https://missionmovement.nl/",
    email: "mission.movement@outlook.com",
    sameAs: ["https://www.instagram.com/mission.movement"],
  },
};

const CONTACT_FALLBACK_EMAIL = "mission.movement@outlook.com";

const buildMailtoUrl = ({
  intentie,
  name,
  email,
  subject,
  message,
  privacyConsent,
  marketingConsent,
}) => {
  const mailSubject = `Mission Movement contact - ${subject || "Nieuw bericht"}`;
  const mailBody = [
    `Intentie: ${intentie || "-"}`,
    `Naam: ${name || "-"}`,
    `E-mail: ${email || "-"}`,
    `Onderwerp/doel: ${subject || "-"}`,
    `Privacy akkoord: ${privacyConsent ? "ja" : "nee"}`,
    `Toestemming e-mail/WhatsApp updates: ${
      marketingConsent ? "ja" : "nee"
    }`,
    "",
    "Bericht:",
    message || "-",
  ].join("\n");

  return `mailto:${CONTACT_FALLBACK_EMAIL}?subject=${encodeURIComponent(
    mailSubject
  )}&body=${encodeURIComponent(mailBody)}`;
};

const Contact = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaReady, setCaptchaReady] = useState(false);
  const captchaRef = useRef(null);

  const CAPTCHA_PROVIDER = process.env.REACT_APP_CAPTCHA_PROVIDER || "hcaptcha";
  const CAPTCHA_SITE_KEY = process.env.REACT_APP_CAPTCHA_SITE_KEY;
  const CONTACT_ENDPOINT = process.env.REACT_APP_CONTACT_ENDPOINT;
  const captchaEnabled = Boolean(CAPTCHA_SITE_KEY);

  useEffect(() => {
    if (!captchaEnabled) {
      return;
    }

    const renderWidget = () => {
      if (!captchaRef.current) return;

      if (CAPTCHA_PROVIDER === "recaptcha" && window.grecaptcha) {
        window.grecaptcha.render(captchaRef.current, {
          sitekey: CAPTCHA_SITE_KEY,
          callback: (token) => setCaptchaToken(token),
          "expired-callback": () => setCaptchaToken(""),
        });
        setCaptchaReady(true);
        return;
      }

      if (window.hcaptcha) {
        window.hcaptcha.render(captchaRef.current, {
          sitekey: CAPTCHA_SITE_KEY,
          theme: "light",
          callback: (token) => setCaptchaToken(token),
          "expired-callback": () => setCaptchaToken(""),
        });
        setCaptchaReady(true);
      }
    };

    const scriptId = `captcha-script-${CAPTCHA_PROVIDER}`;
    if (document.getElementById(scriptId)) {
      renderWidget();
      return;
    }

    const script = document.createElement("script");
    script.id = scriptId;
    script.async = true;
    script.defer = true;
    script.src =
      CAPTCHA_PROVIDER === "recaptcha"
        ? "https://www.google.com/recaptcha/api.js?render=explicit"
        : "https://js.hcaptcha.com/1/api.js?render=explicit";
    script.onload = renderWidget;
    document.body.appendChild(script);
  }, [CAPTCHA_PROVIDER, CAPTCHA_SITE_KEY, captchaEnabled]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    const formData = new FormData(event.currentTarget);
    const payload = {
      intentie: formData.get("intentie"),
      name: formData.get("name"),
      email: formData.get("email"),
      subject: formData.get("subject"),
      message: formData.get("message"),
      privacyConsent: formData.get("privacyConsent") === "on",
      marketingConsent: formData.get("marketingConsent") === "on",
    };

    if (!payload.privacyConsent) {
      setError(
        "Akkoord met de privacyverklaring is nodig om je bericht te kunnen beantwoorden."
      );
      return;
    }

    const mailtoUrl = buildMailtoUrl(payload);

    if (!CONTACT_ENDPOINT) {
      window.location.href = mailtoUrl;
      setSuccess(
        "Je e-mailprogramma is geopend. Verstuur het bericht daar om contact op te nemen."
      );
      return;
    }

    if (captchaEnabled && !captchaToken) {
      setError("Bevestig eerst de CAPTCHA.");
      return;
    }

    if (captchaEnabled) {
      payload.captchaToken = captchaToken;
    }

    setLoading(true);

    try {
      const response = await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        window.location.href = mailtoUrl;
        setError(
          "Het formulier kon niet automatisch verzenden. Je e-mailprogramma is geopend als alternatief."
        );
        return;
      }

      setSuccess("Bedankt! Je bericht is verstuurd.");
      event.currentTarget.reset();
      setCaptchaToken("");
    } catch {
      window.location.href = mailtoUrl;
      setSuccess(
        "Het formulier kon niet automatisch verzenden. Je e-mailprogramma is geopend als alternatief."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Helmet>
        <title>Contact | Start jouw Voorbereiding met Mission Movement</title>
        <meta
          name="description"
          content="Neem contact op met Mission Movement voor gerichte begeleiding richting Defensie, politie, brandweer, KMar of specialistische eenheden."
        />
        <meta
          property="og:title"
          content="Contact | Start jouw Voorbereiding met Mission Movement"
        />
        <meta
          property="og:description"
          content="Neem contact op met Mission Movement voor gerichte begeleiding richting Defensie, politie, brandweer, KMar of specialistische eenheden."
        />
        <meta
          property="og:image"
          content="https://missionmovement.nl/img/contactus.png"
        />
        <meta
          property="og:url"
          content="https://missionmovement.nl/contact"
        />
        <link
          rel="canonical"
          href="https://missionmovement.nl/contact"
        />
        <script type="application/ld+json">
          {JSON.stringify(contactPageSchema)}
        </script>
      </Helmet>

      <PublicHero
        eyebrow="Contact"
        title="Vragen?"
        subtitle="Wil je weten welke voorbereiding past bij jouw doel? Stuur je gegevens door en ik neem persoonlijk contact met je op."
        backgroundClass="bg-takethestep bg-center bg-no-repeat bg-cover bg-fixed"
      />

      {/* Introtekst */}
      <section className="w-full px-5 sm:px-10 lg:px-20 py-12 md:py-14 flex flex-col justify-center items-center">
        <h1 className="mm-h1 text-mmText mb-6 text-center">Jouw Eerste Stap</h1>
        <p className="text-mmTextMuted font-normal md:text-lg text-center max-w-2xl text-base leading-relaxed">
          Vertel kort waar je naartoe werkt en waar je nu staat. Dan kijk ik
          met je mee welke route logisch is. Twijfel je welke route past? Dan
          kijk ik met je mee of je beter zelfstandig 12 weken kunt trainen,
          persoonlijk begeleid moet worden, of eerst je startpunt scherper moet
          krijgen. Twijfel je over pijnsignalen, eerdere blessures, je
          startniveau of belastbaarheid? Vermeld dat kort in je bericht, dan
          kan ik gerichter met je meekijken.
        </p>
      </section>

      {/* Contactformulier */}
      <section
        id="contact-form"
        className="w-full px-5 py-8 md:py-12 lg:py-16 flex justify-center items-center scroll-mt-24 md:scroll-mt-28 lg:scroll-mt-32"
      >
        <div className="w-full max-w-5xl bg-mmSurface border border-mmBorder p-5 md:p-7 rounded-2xl shadow-sm animate-fade-in grid grid-cols-1 lg:grid-cols-[1.4fr_0.8fr] gap-7 lg:gap-8">
          <div>
            <h2 className="mm-h2 text-mmText mb-3">Plan je eerste contact</h2>
            <p className="text-mmTextMuted text-sm md:text-base leading-relaxed mb-5">
              Vertel kort waar je naartoe werkt en waar je nu staat. Dan kijk
              ik welke route logisch is: zelfstandig 12 weken trainen,
              persoonlijk begeleid worden, of eerst je startpunt scherper
              krijgen. Twijfel je over pijnsignalen, eerdere blessures, je
              startniveau of belastbaarheid? Vermeld dat kort in je bericht.
            </p>

            <form
              onSubmit={handleSubmit}
              className="flex flex-col gap-y-4"
              autoComplete="off"
            >
              {/* Intentie */}
              <select
                name="intentie"
                required
                className="bg-transparent border-b border-mmBorder rounded-md h-[46px] outline-none font-normal text-mmText px-3 placeholder:text-mmTextMuted focus:border-mmAccent focus:ring-1 focus:ring-mmFocus transition duration-200"
              >
                <option value="">Waarvoor neem je contact op?</option>
                <option value="Zelfstandig 12-weeks programma - militaire Defensie-route">
                  Zelfstandig 12-weeks programma, militaire Defensie-route
                </option>
                <option value="Persoonlijk 12-weeks traject - Defensie">
                  Persoonlijk 12-weeks traject, Defensie
                </option>
                <option value="Persoonlijk 12-weeks traject - veiligheidsdiensten">
                  Persoonlijk 12-weeks traject, veiligheidsdiensten
                </option>
                <option value="Algemene vraag / intake">
                  Algemene vraag / intake
                </option>
              </select>

              {/* Naam & E-mail */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  name="name"
                  placeholder="Naam"
                  required
                  className="bg-transparent border-b border-mmBorder rounded-md h-[46px] outline-none font-normal w-full px-3 placeholder:text-mmTextMuted text-mmText focus:border-mmAccent focus:ring-1 focus:ring-mmFocus transition duration-200"
                />
                <input
                  type="email"
                  name="email"
                  placeholder="E-mailadres"
                  required
                  className="bg-transparent border-b border-mmBorder rounded-md h-[46px] outline-none font-normal w-full px-3 placeholder:text-mmTextMuted text-mmText focus:border-mmAccent focus:ring-1 focus:ring-mmFocus transition duration-200"
                />
              </div>

              {/* Onderwerp */}
              <input
                type="text"
                name="subject"
                placeholder="Onderwerp of doel"
                required
                className="bg-transparent border-b border-mmBorder rounded-md h-[46px] outline-none font-normal w-full px-3 placeholder:text-mmTextMuted text-mmText focus:border-mmAccent focus:ring-1 focus:ring-mmFocus transition duration-200"
              />

              {/* Bericht */}
              <textarea
                name="message"
                placeholder="Vertel kort waar je naartoe werkt, wanneer je wilt starten en waar je nu tegenaan loopt..."
                required
                className="bg-transparent border-b border-mmBorder rounded-md h-[108px] outline-none font-normal w-full px-3 py-3 placeholder:text-mmTextMuted text-mmText focus:border-mmAccent focus:ring-1 focus:ring-mmFocus transition duration-200 resize-none"
              />

              {captchaEnabled && (
                <div className="flex flex-col gap-2">
                  <div ref={captchaRef} />
                  {!captchaReady && (
                    <p className="text-xs text-gray-400">
                      CAPTCHA wordt geladen...
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-3 rounded-xl border border-mmBorder bg-bg/30 p-4">
                <label className="flex items-start gap-3 text-sm leading-relaxed text-mmTextMuted">
                  <input
                    type="checkbox"
                    name="privacyConsent"
                    className="mt-1 h-4 w-4 shrink-0 rounded border-mmBorderStrong bg-transparent accent-mmAccent"
                  />
                  <span>
                    Ik ga akkoord dat Mission Movement mijn gegevens gebruikt om
                    mijn bericht te beantwoorden. Ik heb de{" "}
                    <Link
                      to="/legal#privacy"
                      className="text-mmAccent hover:opacity-80"
                    >
                      privacyverklaring
                    </Link>{" "}
                    gelezen.
                  </span>
                </label>

                <label className="flex items-start gap-3 text-sm leading-relaxed text-mmTextMuted">
                  <input
                    type="checkbox"
                    name="marketingConsent"
                    className="mt-1 h-4 w-4 shrink-0 rounded border-mmBorderStrong bg-transparent accent-mmAccent"
                  />
                  <span>
                    Mission Movement mag mij per e-mail of WhatsApp benaderen
                    met updates over de lancering, aanbod en relevante
                    voorbereiding.
                  </span>
                </label>
              </div>

              {error && (
                <p className="text-red-400 text-sm md:text-base">{error}</p>
              )}
              {success && (
                <p className="text-green-500 text-sm md:text-base">
                  {success}
                </p>
              )}

              {/* Verstuur */}
              <button
                type="submit"
                disabled={loading}
                className="mm-btnPrimary mt-1 flex items-center justify-center gap-2 disabled:opacity-50 relative overflow-hidden w-full sm:w-fit"
              >
                {loading ? (
                  <div className="w-6 h-6 flex items-center justify-center">
                    <Spinner color="border-mmAccent" />
                  </div>
                ) : (
                  "Verstuur"
                )}
              </button>
            </form>
          </div>

          <aside className="border border-mmBorder bg-bg/40 rounded-xl p-5 flex flex-col justify-between gap-5">
            <div>
              <h3 className="font-display uppercase tracking-widest text-mmAccent text-lg mb-3">
                Direct contact
              </h3>
              <p className="text-mmTextMuted text-sm leading-relaxed mb-5">
                Wil je sneller schakelen? Stuur me direct een WhatsApp of
                e-mail.
              </p>
              <div className="flex flex-col gap-3">
                <a
                  href="https://wa.me/31649171684"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mm-btn w-full"
                  onClick={() =>
                    trackContactClick({
                      method: "whatsapp",
                      location: "contact_page",
                    })
                  }
                >
                  Stuur een WhatsApp
                </a>
                <a
                  href="mailto:mission.movement@outlook.com"
                  className="mm-btn w-full"
                  onClick={() =>
                    trackContactClick({
                      method: "email",
                      location: "contact_page",
                    })
                  }
                >
                  Stuur een e-mail
                </a>
              </div>
            </div>

            <div className="space-y-4">
              <ul className="space-y-2 text-sm text-mmTextMuted">
                {[
                  "Persoonlijk antwoord",
                  "Geen automatische funnel",
                  "Geschikt voor Defensie en Veiligheidsdiensten",
                ].map((item) => (
                  <li key={item} className="flex gap-2">
                    <span className="text-mmAccent">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-mmTextMuted leading-relaxed">
                Mission Movement is onafhankelijk en is niet verbonden aan
                Defensie, politie, brandweer, KMar of andere overheidsdiensten.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default Contact;
